/**
 * Symmetric 3x3 eigendecomposition.
 *
 * Two independent implementations live here:
 *
 *  - `jacobiEigen` — cyclic Jacobi, the default. For a 3x3 it converges in a
 *    few sweeps and reaches machine precision, which is what the tests assert
 *    (`||E L E^T - C|| < 1e-9`, `||E^T E - I|| < 1e-12`).
 *  - `eigenClosedForm` — the analytic trigonometric solution (Smith 1961), used
 *    as a cross-check in tests. Hypothesis T5 in the plan.
 *
 * Both return eigenvalues in **descending** order and orthonormal eigenvectors
 * as the columns of `vectors`, i.e. `vectors[c*3+r]`, matching core/mat3.ts.
 *
 * ## Sign convention
 *
 * An eigenvector's sign is arbitrary. Two deterministic rules pin it down:
 *
 *  1. The component with the largest absolute value is made positive.
 *  2. `det(vectors)` is forced to `+1`, by flipping the last (smallest-lambda)
 *     eigenvector if needed.
 *
 * Rule 2 means the result is a rotation, never a reflection. That matters for
 * `DStretch`-style band orderings, and it makes the build manifest byte-stable.
 *
 * @module core/jacobi
 */

import type { Eigen, Mat3, Vec3 } from './types.ts';
import { get, identity, mul, set, transpose, trace, det } from './mat3.ts';

/** Maximum sweeps before `jacobiEigen` gives up. Three is generous for 3x3. */
const MAX_SWEEPS = 32;

/**
 * Symmetric eigendecomposition by cyclic Jacobi rotations.
 *
 * @param c Symmetric 3x3, column-major. Only the symmetric part is used.
 */
export function jacobiEigen(c: Mat3, epsilon = 1e-15): Eigen {
  // Working copy of the symmetric part.
  const a = new Float64Array(9);
  for (let r = 0; r < 3; r++) {
    for (let col = r; col < 3; col++) {
      // Average the two off-diagonal entries, so a slightly asymmetric input
      // still yields a sensible symmetric problem.
      const v = r === col ? get(c, r, col) : 0.5 * (get(c, r, col) + get(c, col, r));
      set(a, r, col, v);
      set(a, col, r, v);
    }
  }

  const v = identity();
  const pairs: ReadonlyArray<readonly [number, number]> = [
    [0, 1],
    [0, 2],
    [1, 2],
  ];

  for (let sweep = 0; sweep < MAX_SWEEPS; sweep++) {
    let off = 0;
    for (const [p, q] of pairs) {
      off += Math.abs(get(a, p, q)) + Math.abs(get(a, q, p));
    }
    if (off < epsilon) break;

    for (const [p, q] of pairs) {
      const apq = get(a, p, q);
      if (Math.abs(apq) < Number.MIN_VALUE) continue;
      const app = get(a, p, p);
      const aqq = get(a, q, q);

      // Rotation that zeroes A[p][q].
      const theta = (aqq - app) / (2 * apq);
      const t =
        (theta >= 0 ? 1 : -1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
      const cos = 1 / Math.sqrt(t * t + 1);
      const sin = t * cos;

      // A <- J^T A J.
      for (let k = 0; k < 3; k++) {
        if (k === p || k === q) continue;
        const akp = get(a, k, p);
        const akq = get(a, k, q);
        const np = cos * akp - sin * akq;
        const nq = sin * akp + cos * akq;
        set(a, k, p, np);
        set(a, p, k, np);
        set(a, k, q, nq);
        set(a, q, k, nq);
      }
      const c2 = cos * cos;
      const s2 = sin * sin;
      const sc = sin * cos;
      set(a, p, p, c2 * app - 2 * sc * apq + s2 * aqq);
      set(a, q, q, s2 * app + 2 * sc * apq + c2 * aqq);
      set(a, p, q, 0);
      set(a, q, p, 0);

      // V <- V J. Column p and column q rotate together.
      for (let r = 0; r < 3; r++) {
        const vrp = get(v, r, p);
        const vrq = get(v, r, q);
        set(v, r, p, cos * vrp - sin * vrq);
        set(v, r, q, sin * vrp + cos * vrq);
      }
    }
  }

  const values = Float64Array.of(get(a, 0, 0), get(a, 1, 1), get(a, 2, 2));

  // Sort descending, permuting the eigenvector columns to match.
  const order = [0, 1, 2].sort((i, j) => values[j]! - values[i]!);
  const sortedValues = Float64Array.of(values[order[0]!]!, values[order[1]!]!, values[order[2]!]!);
  const sortedVectors = new Float64Array(9);
  for (let dst = 0; dst < 3; dst++) {
    const src = order[dst]!;
    for (let r = 0; r < 3; r++) sortedVectors[dst * 3 + r] = get(v, r, src);
  }

  return { values: sortedValues, vectors: applySignConvention(sortedVectors, sortedValues) };
}

/**
 * Analytic eigenvalues and eigenvectors of a symmetric 3x3 (Smith 1961).
 *
 * Only valid for symmetric input. Provided as an independent check on the
 * iterative path, and as hypothesis T5.
 */
export function eigenClosedForm(c: Mat3): Eigen {
  const a = new Float64Array(9);
  for (let r = 0; r < 3; r++) {
    for (let col = r; col < 3; col++) {
      const val = r === col ? get(c, r, col) : 0.5 * (get(c, r, col) + get(c, col, r));
      set(a, r, col, val);
      set(a, col, r, val);
    }
  }

  const p1 = get(a, 0, 1) ** 2 + get(a, 0, 2) ** 2 + get(a, 1, 2) ** 2;
  if (p1 < 1e-300) {
    // Already diagonal: the eigenvectors are the standard basis, permuted to
    // match the descending eigenvalue order.
    const values = Float64Array.of(get(a, 0, 0), get(a, 1, 1), get(a, 2, 2));
    const order = [0, 1, 2].sort((i, j) => values[j]! - values[i]!);
    const sortedVectors = new Float64Array(9);
    for (let d = 0; d < 3; d++) sortedVectors[d * 3 + order[d]!] = 1;
    const sortedValues = Float64Array.of(
      values[order[0]!]!,
      values[order[1]!]!,
      values[order[2]!]!,
    );
    return { values: sortedValues, vectors: applySignConvention(sortedVectors, sortedValues) };
  }

  const q = trace(a) / 3;
  const b00 = get(a, 0, 0) - q;
  const b11 = get(a, 1, 1) - q;
  const b22 = get(a, 2, 2) - q;
  const p2 = b00 * b00 + b11 * b11 + b22 * b22 + 2 * p1;
  const p = Math.sqrt(p2 / 6);
  if (p === 0) {
    return { values: Float64Array.of(q, q, q), vectors: identity() };
  }

  // B = (A - qI) / p; its determinant is 2 * cos(3*phi) clamped to [-1, 1].
  const inv = 1 / p;
  const b = new Float64Array(9);
  for (let r = 0; r < 3; r++) {
    for (let col = 0; col < 3; col++) {
      set(b, r, col, get(a, r, col) * inv);
    }
  }
  set(b, 0, 0, b00 * inv);
  set(b, 1, 1, b11 * inv);
  set(b, 2, 2, b22 * inv);

  let r = det(b) / 2;
  r = Math.max(-1, Math.min(1, r));
  const phi = Math.acos(r) / 3;

  const eig1 = q + 2 * p * Math.cos(phi);
  const eig3 = q + 2 * p * Math.cos(phi + (2 * Math.PI) / 3);
  const eig2 = 3 * q - eig1 - eig3;

  const values = Float64Array.of(eig1, eig2, eig3);

  // Eigenvectors by inverse iteration on (A - lambda I + eps I). Robust for the
  // 3x3 case, and cheaper than a second closed-form branch for the degenerate
  // case where two eigenvalues coincide.
  const vectors = new Float64Array(9);
  const shift = p * 1e-9;
  for (let k = 0; k < 3; k++) {
    const vec = inverseIterate(a, values[k]!, shift);
    vectors[k * 3 + 0] = vec[0]!;
    vectors[k * 3 + 1] = vec[1]!;
    vectors[k * 3 + 2] = vec[2]!;
  }
  reorthonormalise(vectors);

  // Sort descending with the vectors.
  const order = [0, 1, 2].sort((i, j) => values[j]! - values[i]!);
  const sortedValues = Float64Array.of(values[order[0]!]!, values[order[1]!]!, values[order[2]!]!);
  const sortedVectors = new Float64Array(9);
  for (let d = 0; d < 3; d++) {
    const src = order[d]!;
    for (let row = 0; row < 3; row++) sortedVectors[d * 3 + row] = vectors[src * 3 + row]!;
  }

  return { values: sortedValues, vectors: applySignConvention(sortedVectors, sortedValues) };
}

/** One step of inverse iteration for `(A - lambda I)`. */
function inverseIterate(a: Mat3, lambda: number, shift: number): Vec3 {
  // Solve (A - (lambda - shift) I) x = v, repeating with the previous x.
  const m = new Float64Array(9);
  for (let i = 0; i < 9; i++) m[i] = a[i]!;
  m[0] = m[0]! - (lambda - shift);
  m[4] = m[4]! - (lambda - shift);
  m[8] = m[8]! - (lambda - shift);

  let x = Float64Array.of(1, 1, 1);
  for (let iter = 0; iter < 3; iter++) {
    const solved = solve3(m, x);
    if (!solved) break;
    x = solved;
  }
  const norm = Math.hypot(x[0]!, x[1]!, x[2]!);
  if (norm === 0 || !Number.isFinite(norm)) return Float64Array.of(1, 0, 0);
  return Float64Array.of(x[0]! / norm, x[1]! / norm, x[2]! / norm);
}

/**
 * Solve `M x = b` for a 3x3 `M`, by Cramer's rule with a NaN-safe fallback.
 * Returns `null` when `M` is singular to working precision.
 */
export function solve3(m: Mat3, b: ArrayLike<number>): Vec3 | null {
  const d = det(m);
  const scale = Math.max(
    Math.abs(m[0]!), Math.abs(m[1]!), Math.abs(m[2]!),
    Math.abs(m[3]!), Math.abs(m[4]!), Math.abs(m[5]!),
    Math.abs(m[6]!), Math.abs(m[7]!), Math.abs(m[8]!),
  );
  if (!Number.isFinite(d) || Math.abs(d) < 1e-14 * Math.max(scale ** 3, 1)) return null;

  const out = new Float64Array(3);
  for (let col = 0; col < 3; col++) {
    const repl = new Float64Array(9);
    for (let i = 0; i < 9; i++) repl[i] = m[i]!;
    for (let row = 0; row < 3; row++) repl[col * 3 + row] = b[row]!;
    out[col] = det(repl) / d;
  }
  return out;
}

/** Modified Gram-Schmidt, in place, on three column vectors. */
function reorthonormalise(v: Mat3): void {
  for (let c = 0; c < 3; c++) {
    let x = Float64Array.of(v[c * 3 + 0]!, v[c * 3 + 1]!, v[c * 3 + 2]!);
    for (let pass = 0; pass < 2; pass++) {
      for (let d = 0; d < c; d++) {
        const dot = x[0]! * v[d * 3 + 0]! + x[1]! * v[d * 3 + 1]! + x[2]! * v[d * 3 + 2]!;
        x[0] = x[0]! - dot * v[d * 3 + 0]!;
        x[1] = x[1]! - dot * v[d * 3 + 1]!;
        x[2] = x[2]! - dot * v[d * 3 + 2]!;
      }
    }
    let norm = Math.hypot(x[0]!, x[1]!, x[2]!);
    if (norm < 1e-12) {
      // Degenerate: pick any vector orthogonal to the ones already placed.
      x = pickOrthogonal(v, c);
      norm = Math.hypot(x[0]!, x[1]!, x[2]!);
    }
    v[c * 3 + 0] = x[0]! / norm;
    v[c * 3 + 1] = x[1]! / norm;
    v[c * 3 + 2] = x[2]! / norm;
  }
}

/** Any unit vector orthogonal to the first `count` columns of `v`. */
function pickOrthogonal(v: Mat3, count: number): Vec3 {
  const candidates: ReadonlyArray<Vec3> = [
    Float64Array.of(1, 0, 0),
    Float64Array.of(0, 1, 0),
    Float64Array.of(0, 0, 1),
  ];
  let best: Vec3 = Float64Array.of(1, 0, 0);
  let bestScore = -1;
  for (const cand of candidates) {
    let x = Float64Array.of(cand[0]!, cand[1]!, cand[2]!);
    for (let d = 0; d < count; d++) {
      const dot = x[0]! * v[d * 3 + 0]! + x[1]! * v[d * 3 + 1]! + x[2]! * v[d * 3 + 2]!;
      x[0] = x[0]! - dot * v[d * 3 + 0]!;
      x[1] = x[1]! - dot * v[d * 3 + 1]!;
      x[2] = x[2]! - dot * v[d * 3 + 2]!;
    }
    const score = Math.hypot(x[0]!, x[1]!, x[2]!);
    if (score > bestScore) {
      bestScore = score;
      best = x;
    }
  }
  return best;
}

/**
 * Apply the deterministic sign convention described in the module comment.
 * Returns a new matrix; does not mutate the input.
 */
export function applySignConvention(vectors: Mat3, _values?: Vec3): Mat3 {
  const out = Float64Array.from(vectors);
  for (let c = 0; c < 3; c++) {
    let bigRow = 0;
    let bigAbs = -1;
    for (let r = 0; r < 3; r++) {
      const abs = Math.abs(out[c * 3 + r]!);
      if (abs > bigAbs) {
        bigAbs = abs;
        bigRow = r;
      }
    }
    if (out[c * 3 + bigRow]! < 0) {
      for (let r = 0; r < 3; r++) out[c * 3 + r] = -out[c * 3 + r]!;
    }
  }
  // Force a rotation (det = +1) rather than a reflection.
  if (det(out) < 0) {
    for (let r = 0; r < 3; r++) out[2 * 3 + r] = -out[2 * 3 + r]!;
  }
  return out;
}

/** `E * diag(lambda) * E^T`. Used by tests to check the decomposition. */
export function reconstruct(e: Eigen): Mat3 {
  const d = new Float64Array(9);
  d[0] = e.values[0]!;
  d[4] = e.values[1]!;
  d[8] = e.values[2]!;
  return mul(mul(e.vectors, d), transpose(e.vectors));
}
